<template>
  <div class="page-container-v2 contract-view-policy">
    <page-header title="전자계약 권한관리" sub-title="CS 엔지니어 명단, 개인 권한, 지점 계보를 관리합니다.">
      <template #rightArea>
        <a-button @click="openVideoGuide">동영상 가이드</a-button>
      </template>
    </page-header>

    <div v-if="forbidden" class="card-v2 forbidden-box">
      전자계약 권한관리는 덴티움 마스터 계정만 사용할 수 있습니다.
    </div>

    <template v-else>
      <a-spin :spinning="loading">
        <template v-if="policy">
          <a-alert
            v-if="policy.activeEvaluator === 'legacy'"
            type="warning"
            show-icon
            class="legacy-banner"
            message="현재 운영 판정기가 기존 방식(legacy)이라 여기서 저장한 내용은 조회 권한에 아직 반영되지 않습니다."
          />

          <div class="card-v2 tabs-card">
            <a-tabs v-model:activeKey="activeTab" @change="onTabChange">
              <!-- 자동 규칙 -->
              <a-tab-pane key="rules" tab="자동 규칙">
                <p class="staff-tab-intro">소속·직급·권한 옵션만으로 모든 직원의 조회 범위가 정해집니다. 예외가 필요할 때만 아래 두 가지를 씁니다.</p>
                <div class="rules-ladder">
                  <p class="rules-ladder__heading">계정은 위에서부터 처음 맞는 조건 하나로 분류됩니다</p>

                  <div class="rules-legend">
                    <a-tag color="purple">전체</a-tag>
                    <a-tag color="green">설치확인서</a-tag>
                    <a-tag color="blue">영업계약 · 지점 범위</a-tag>
                    <a-tag>본인 발행</a-tag>
                  </div>

                  <div class="rules-probe">
                    <span class="rules-probe__label">계정으로 확인</span>
                    <a-select
                      v-model:value="rulesProbe.username"
                      show-search
                      allow-clear
                      :filter-option="false"
                      :loading="rulesProbe.loading"
                      placeholder="사번 또는 이름으로 검색"
                      style="width: 280px"
                      @search="onAccountSearch"
                      @change="onRulesProbeSelect"
                    >
                      <template #notFoundContent>검색 결과가 없습니다</template>
                      <a-select-option v-for="acc in accountOptions" :key="acc.username" :value="acc.username">
                        {{ accountLabel(acc) }}
                      </a-select-option>
                    </a-select>
                    <a v-if="rulesProbe.username || rulesProbe.result" class="rules-step__link" @click="onRulesProbeClear">지우기</a>
                  </div>

                  <p v-if="rulesProbe.result" class="rules-probe__summary">
                    {{ rulesProbeSummary }}
                    <a-tag v-if="rulesProbe.result.evaluator !== 'policy'" color="warning">현재 판정기: legacy — 아래 경로는 정책 구조 기준입니다</a-tag>
                    <a-tag v-if="rulesProbe.result.account && !rulesProbe.result.account.active">비활성 계정</a-tag>
                  </p>
                  <div class="rules-ladder__steps">
                    <template v-for="(step, index) in RULE_STEPS" :key="step.id">
                      <div class="rules-step" :class="rulesStepClass(step, index)">
                        <span class="rules-step__badge">{{ index + 1 }}</span>
                        <div class="rules-step__condition">
                          <template v-if="step.id === 'CS_TEAM'">
                            소속 = 고객지원팀 또는
                            <a class="rules-step__link" @click="goToTab('cs')">CS 엔지니어 명단 {{ policy.csEngineers.items.length }}명</a>
                          </template>
                          <a-tooltip v-else-if="step.tooltip" :title="step.tooltip">{{ step.condition }}</a-tooltip>
                          <template v-else>{{ step.condition }}</template>
                        </div>
                        <div class="rules-step__branch">
                          <span class="rules-step__branch-label">예</span>
                          <span class="rules-step__arrow">→</span>
                          <a-tag :color="step.tagColor">{{ step.result }}</a-tag>
                        </div>
                      </div>
                      <div class="rules-connector" :class="rulesConnectorClass(index)">
                        <span class="rules-connector__line"></span>
                        <span class="rules-connector__label">아니오</span>
                      </div>
                    </template>

                    <div class="rules-step" :class="rulesFallbackClass()">
                      <span class="rules-step__badge rules-step__badge--terminal">▼</span>
                      <div class="rules-step__condition">그 외</div>
                      <div class="rules-step__branch">
                        <span class="rules-step__arrow">→</span>
                        <a-tag>본인이 발행한 영업계약</a-tag>
                      </div>
                    </div>
                  </div>

                  <div class="rules-addons">
                    <div class="rules-addon-card" :class="{ 'rules-addon-card--highlight': rulesProbe.result && rulesProbe.result.appliedGrants && rulesProbe.result.appliedGrants.length }">
                      <div class="rules-addon-card__head">
                        <span class="rules-addon-card__title">＋ 개인 예외 {{ (policy.personalGrants || []).length }}건</span>
                        <a class="rules-step__link" @click="goToTab('grants')">관리 →</a>
                      </div>
                      <p class="rules-addon-card__desc">기본 결과에 더해집니다(줄이지 않음): {{ SCOPE_LABELS.SALES_DEPTS }} / {{ SCOPE_LABELS.CS_ALL }}</p>
                      <ul v-if="personalGrantsPreview.shown.length" class="rules-addon-card__applied">
                        <li v-for="g in personalGrantsPreview.shown" :key="g.id">{{ personalGrantLine(g) }}</li>
                        <li v-if="personalGrantsPreview.restCount > 0" class="staff-more">외 {{ personalGrantsPreview.restCount }}건</li>
                      </ul>
                    </div>
                    <div class="rules-addon-card" :class="{ 'rules-addon-card--highlight': rulesProbe.result && rulesProbe.result.appliedLineages && rulesProbe.result.appliedLineages.length }">
                      <div class="rules-addon-card__head">
                        <span class="rules-addon-card__title">＋ 지점 변경 연결 {{ (policy.deptLineages || []).length }}건</span>
                        <a class="rules-step__link" @click="goToTab('lineages')">관리 →</a>
                      </div>
                      <p class="rules-addon-card__desc">지점 범위(5~7번, 개인 권한의 지점)에 폐지 지점을 포함합니다</p>
                      <ul v-if="deptLineagesPreview.shown.length" class="rules-addon-card__applied">
                        <li v-for="l in deptLineagesPreview.shown" :key="l.id">{{ lineageSummary(l) }}</li>
                        <li v-if="deptLineagesPreview.restCount > 0" class="staff-more">외 {{ deptLineagesPreview.restCount }}건</li>
                      </ul>
                    </div>
                  </div>

                  <p class="rules-footnote">"영업계약" = 설치확인서 2종을 뺀 전 유형.</p>
                </div>
              </a-tab-pane>

              <!-- 직원별 조회 권한 -->
              <a-tab-pane key="grants" tab="직원별 조회 권한">
                <p class="staff-tab-intro">활성 직원 전원의 현재 조회 범위입니다(자동 규칙 + 지점 변경 연결 + 개인 예외를 합친 결과). 특정 직원 한 명에게만 범위를 더 줘야 할 때 이 탭에서 '예외 추가'를 씁니다. 지점이 나뉘거나 이름이 바뀐 경우는 '지점 변경 연결'에서 처리하세요(그 지점 전원에게 자동 적용).</p>

                <div class="staff-toolbar">
                  <div class="staff-toolbar__filters">
                    <a-input
                      v-model:value="staffFilters.query"
                      placeholder="사번·이름·소속 검색"
                      allow-clear
                      style="width: 240px"
                      @change="onStaffQueryInput"
                      @press-enter="onStaffQueryEnter"
                    />
                    <a-select v-model:value="staffFilters.classification" style="width: 160px" @change="onStaffFilterChange">
                      <a-select-option value="">전체</a-select-option>
                      <a-select-option v-for="key in classificationKeys" :key="key" :value="key">{{ CLASSIFICATION_LABELS[key] }}</a-select-option>
                    </a-select>
                    <a-checkbox v-model:checked="staffFilters.onlyExceptions" @change="onStaffFilterChange">개인 예외 있는 직원만</a-checkbox>
                  </div>
                  <a-button type="primary" @click="openAddModal('grant')">개인 예외 추가</a-button>
                </div>

                <a-table
                  :columns="staffColumns"
                  :data-source="staffTable.items"
                  :pagination="staffPagination"
                  :loading="staffTable.loading"
                  size="small"
                  row-key="username"
                  :scroll="{ x: 'max-content' }"
                  :expanded-row-keys="staffExpandedRowKeys"
                  :locale="{ emptyText: '조건에 맞는 직원이 없습니다' }"
                  @expand="onStaffExpand"
                  @change="onStaffTableChange"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'scope'">{{ scopeSentence(record.scope) }}</template>
                    <template v-else-if="column.key === 'exceptions'">
                      <template v-if="record.exceptions > 0 || (record.lineages && record.lineages.length)">
                        <a-tag v-if="record.exceptions > 0" color="blue">개인 예외 {{ record.exceptions }}</a-tag>
                        <a-tag v-if="record.lineages && record.lineages.length" class="staff-tag-small">옛 지점 포함</a-tag>
                      </template>
                      <template v-else>—</template>
                    </template>
                    <template v-else-if="column.key === 'actions'">
                      <a class="link-action" @click="onStaffExpand(!staffExpandedRowKeys.includes(record.username), record)">상세</a>
                      <a class="link-action" @click="openGrantAddForStaff(record)">예외 추가</a>
                    </template>
                  </template>
                  <template #expandedRowRender="{ record }">
                    <div class="staff-detail">
                      <p class="staff-detail__line">판정: {{ record.step }}번 · {{ CLASSIFICATION_LABELS[record.classification] || record.classification }}</p>
                      <p class="staff-detail__line">{{ scopeSentence(record.scope) }}</p>
                      <div v-if="record.grants && record.grants.length" class="staff-detail__block">
                        <p class="staff-detail__label">개인 예외</p>
                        <ul class="staff-detail__list">
                          <li v-for="g in record.grants" :key="g.id">
                            {{ SCOPE_LABELS[g.scope] || g.scope }} · {{ (g.depts && g.depts.length) ? g.depts.join(', ') : '전사' }} · 결속 {{ g.boundDept || '-' }}
                            <a-tag :color="g.valid ? 'green' : 'red'">{{ g.valid ? '유효' : '무효' }}</a-tag>
                            <a class="link-action" @click="onRemoveGrant({ ...g, username: record.username, name: record.name })">삭제</a>
                          </li>
                        </ul>
                      </div>
                      <div v-if="record.lineages && record.lineages.length" class="staff-detail__block">
                        <p class="staff-detail__label">적용된 지점 변경 연결</p>
                        <ul class="staff-detail__list">
                          <li v-for="l in record.lineages" :key="l.id">{{ lineageSummary(l) }}</li>
                        </ul>
                      </div>
                      <a class="link-action" @click="onViewJudgmentPath(record)">판정 경로 보기 →</a>
                    </div>
                  </template>
                </a-table>
              </a-tab-pane>

              <!-- 지점 변경 연결 -->
              <a-tab-pane key="lineages" tab="지점 변경 연결">
                <p class="staff-tab-intro">지점이 나뉘거나 이름이 바뀌어도 옛 지점명으로 작성된 계약을 후속 지점에서 볼 수 있게 연결합니다. 해당 지점 범위를 보는 사람 전원에게 자동 적용됩니다.</p>

                <div class="tab-toolbar">
                  <a-button type="primary" @click="openAddModal('lineage')">지점 계보 추가</a-button>
                </div>

                <div class="staff-lineage-list">
                  <div v-for="l in policy.deptLineages" :key="l.id" class="staff-lineage-card">
                    <p class="staff-lineage-card__main">
                      {{ l.fromDept }} → {{ l.toDepts.join(', ') }} (발령 {{ l.effectiveDate }}) — 옛 {{ l.fromDept }} 명의 계약이 후속 지점 범위에 포함됩니다
                      <a-tag v-if="l.fromDeptInCurrentOrg" color="warning">현행 조직도에 다시 존재 — 확인 필요</a-tag>
                    </p>
                    <p class="staff-lineage-card__meta">사유: {{ l.reason }}</p>
                    <p class="staff-lineage-card__meta">등록자 {{ l.createdBy }} · 등록 시각 {{ formatKst(l.createdAt) }} (KST)</p>
                    <a class="link-action" @click="onRemoveLineage(l)">삭제</a>
                  </div>
                  <p v-if="!policy.deptLineages.length" class="no-affected">등록된 지점 계보가 없습니다</p>
                </div>
              </a-tab-pane>

              <!-- 설치확인서 전체 조회 명단 -->
              <a-tab-pane key="cs" tab="설치확인서 전체 조회 명단">
                <p class="staff-tab-intro">지점 소속이지만 설치확인서 전체를 봐야 하는 CS 엔지니어 명단입니다.</p>
                <a-alert
                  v-if="policy.csEngineers.usesDefault"
                  type="info"
                  show-icon
                  class="tab-note"
                  message="코드 기본 명단 사용 중 — 첫 저장 시 이 명단이 데이터로 옮겨집니다."
                />

                <div class="tab-toolbar">
                  <a-button type="primary" @click="openAddModal('cs')">CS 엔지니어 추가</a-button>
                </div>

                <a-table
                  :columns="csColumns"
                  :data-source="policy.csEngineers.items"
                  :pagination="false"
                  size="small"
                  row-key="username"
                  :scroll="{ x: 'max-content' }"
                  :locale="{ emptyText: '등록된 CS 엔지니어가 없습니다' }"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'active'">
                      <a-tag :color="record.active ? 'green' : 'default'">{{ record.active ? '활성' : '비활성(퇴사·정지·계정 없음)' }}</a-tag>
                    </template>
                    <template v-else-if="column.key === 'actions'">
                      <a-button danger size="small" :disabled="submitting" @click="onRemoveCs(record)">삭제</a-button>
                    </template>
                  </template>
                </a-table>
              </a-tab-pane>

              <!-- 변경 이력 -->
              <a-tab-pane key="history" tab="변경 이력">
                <a-table
                  :columns="historyColumns"
                  :data-source="policy.history"
                  :pagination="{ pageSize: 20 }"
                  size="small"
                  :row-key="(_r, i) => i"
                  :scroll="{ x: 'max-content' }"
                  :locale="{ emptyText: '변경 이력이 없습니다' }"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'at'">{{ formatKst(record.at) }}</template>
                    <template v-else-if="column.key === 'action'">{{ ACTION_LABELS[record.action] || record.action }}</template>
                  </template>
                </a-table>
              </a-tab-pane>
            </a-tabs>
          </div>
        </template>
        <div v-else style="min-height: 200px"></div>
      </a-spin>

      <!-- 추가/삭제 6종 공용 모달 — step 1(입력) → step 2(변경 미리보기) -->
      <a-modal
        :open="actionModal.visible"
        :title="actionModalTitle"
        width="640px"
        wrap-class-name="contract-view-policy-modal"
        :centered="true"
        :keyboard="false"
        :mask-closable="false"
        destroy-on-close
        :footer="null"
        @cancel="closeActionModal"
      >
      <div class="policy-modal-body">
        <!-- step 1 · 추가 입력 -->
        <template v-if="actionModal.step === 1 && actionModal.mode === 'add'">
          <a-form layout="vertical">
            <template v-if="actionModal.type === 'grant'">
              <a-form-item label="계정 검색" required>
                <a-select
                  v-model:value="grantAddForm.username"
                  show-search
                  allow-clear
                  :filter-option="false"
                  placeholder="사번 또는 이름으로 검색"
                  style="width: 100%"
                  @search="onAccountSearch"
                >
                  <template #notFoundContent>검색 결과가 없습니다</template>
                  <a-select-option v-for="acc in accountOptions" :key="acc.username" :value="acc.username">
                    {{ accountLabel(acc) }}
                  </a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item label="유형" required>
                <a-select v-model:value="grantAddForm.scope" style="width: 100%">
                  <a-select-option value="SALES_DEPTS">{{ SCOPE_LABELS.SALES_DEPTS }}</a-select-option>
                  <a-select-option value="CS_ALL">{{ SCOPE_LABELS.CS_ALL }}</a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item v-if="grantAddForm.scope === 'SALES_DEPTS'" label="지점 (최대 10개)" required>
                <a-select
                  v-model:value="grantAddForm.depts"
                  mode="multiple"
                  style="width: 100%"
                  placeholder="지점 선택"
                  @change="(v) => onDeptsChange(grantAddForm, 'depts', v)"
                >
                  <a-select-option v-for="dept in grantDeptOptions" :key="dept" :value="dept">{{ dept }}</a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item label="사유" required>
                <a-textarea
                  v-model:value="grantAddForm.reason"
                  :maxlength="300"
                  show-count
                  :rows="3"
                  placeholder="요청자·근거를 남겨 주세요. 변경 이력에 기록됩니다."
                />
              </a-form-item>
            </template>
            <template v-else-if="actionModal.type === 'lineage'">
              <a-form-item label="폐지 지점" required>
                <a-select
                  v-model:value="lineageAddForm.fromDept"
                  show-search
                  allow-clear
                  option-filter-prop="value"
                  placeholder="폐지 지점 선택"
                  style="width: 100%"
                >
                  <a-select-option v-for="dept in options.retiredDepts" :key="dept" :value="dept">{{ dept }}</a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item label="후속 지점 (최대 10개)" required>
                <a-select
                  v-model:value="lineageAddForm.toDepts"
                  mode="multiple"
                  style="width: 100%"
                  placeholder="후속 지점 선택"
                  @change="(v) => onDeptsChange(lineageAddForm, 'toDepts', v)"
                >
                  <a-select-option v-for="dept in options.currentDepts" :key="dept" :value="dept">{{ dept }}</a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item label="발령일 (KST)" required>
                <a-date-picker
                  v-model:value="lineageAddForm.effectiveDate"
                  value-format="YYYY-MM-DD"
                  :locale="locale"
                  style="width: 100%"
                  placeholder="발령일"
                />
              </a-form-item>
              <a-form-item label="사유" required>
                <a-textarea
                  v-model:value="lineageAddForm.reason"
                  :maxlength="300"
                  show-count
                  :rows="3"
                  placeholder="요청자·근거를 남겨 주세요. 변경 이력에 기록됩니다."
                />
              </a-form-item>
            </template>
            <template v-else-if="actionModal.type === 'cs'">
              <a-form-item label="계정 검색" required>
                <a-select
                  v-model:value="csAddForm.username"
                  show-search
                  allow-clear
                  :filter-option="false"
                  placeholder="사번 또는 이름으로 검색"
                  style="width: 100%"
                  @search="onAccountSearch"
                >
                  <template #notFoundContent>검색 결과가 없습니다</template>
                  <a-select-option v-for="acc in accountOptions" :key="acc.username" :value="acc.username">
                    {{ accountLabel(acc) }}
                  </a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item label="사유" required>
                <a-textarea
                  v-model:value="csAddForm.reason"
                  :maxlength="300"
                  show-count
                  :rows="3"
                  placeholder="요청자·근거를 남겨 주세요. 변경 이력에 기록됩니다."
                />
              </a-form-item>
            </template>
          </a-form>
          <div class="modal-actions">
            <a-button :disabled="submitting" @click="closeActionModal">취소</a-button>
            <a-button type="primary" :loading="submitting" @click="onActionPreview">미리보기</a-button>
          </div>
        </template>

        <!-- step 1 · 삭제 사유 입력 -->
        <template v-else-if="actionModal.step === 1 && actionModal.mode === 'delete'">
          <p class="modal-summary">{{ actionModal.summary }}</p>
          <a-textarea v-model:value="actionModal.reason" :maxlength="300" show-count :rows="3" placeholder="삭제 사유를 입력해 주세요" />
          <div class="modal-actions">
            <a-button :disabled="submitting" @click="closeActionModal">취소</a-button>
            <a-button type="primary" :loading="submitting" @click="onActionPreview">미리보기</a-button>
          </div>
        </template>

        <!-- step 2 · 변경 미리보기 (추가/삭제 공용) -->
        <template v-else>
          <p class="modal-summary">{{ actionModal.summary }}</p>
          <a-table
            :columns="previewColumns"
            :data-source="actionModal.affected"
            :pagination="false"
            size="small"
            row-key="username"
            :scroll="{ x: 'max-content', y: 360 }"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'classification'">{{ CLASSIFICATION_LABELS[record.classification] || record.classification }}</template>
              <template v-else-if="column.key === 'count'">
                <span :class="['count-delta', { up: record.after > record.before, down: record.after < record.before }]">
                  {{ record.before }} → {{ record.after }}
                </span>
              </template>
            </template>
          </a-table>
          <p v-if="!actionModal.affected.length" class="no-affected">조회 결과가 바뀌는 계정이 없습니다.</p>
          <div class="modal-actions">
            <a-button :disabled="submitting" @click="actionModal.step = 1">뒤로</a-button>
            <a-button type="primary" :danger="actionModal.mode === 'delete'" :loading="submitting" @click="onActionConfirm">
              {{ actionModal.mode === 'delete' ? '삭제 확정' : '확정' }}
            </a-button>
          </div>
        </template>
      </div>
      </a-modal>

      <VideoGuideModal v-model:open="videoGuideOpen" :guide="videoGuide" />
    </template>
  </div>
</template>

<script>
import { message } from 'ant-design-vue';
import locale from 'ant-design-vue/es/date-picker/locale/ko_KR';
import PageHeader from '@/views/components/PageHeader.vue';
import VideoGuideModal from './VideoGuideModal.vue';
import {
  getPolicy,
  getOptions,
  searchAccounts,
  evaluateAccount,
  addCsEngineer,
  removeCsEngineer,
  addPersonalGrant,
  removePersonalGrant,
  addDeptLineage,
  removeDeptLineage,
  listAccountEvaluations,
} from './api';
import { getVideoGuide } from './videoGuide';

// 계정 검색 디바운스 — BrightmallOrders.vue 관례와 동일하게 500ms
const DEBOUNCE_MS = 500;
// 직원별 조회 권한 탭 검색 디바운스
const STAFF_SEARCH_DEBOUNCE_MS = 300;

const TAB_KEYS = ['rules', 'grants', 'lineages', 'cs', 'history'];

// 서버 시각(UTC ISO)을 브라우저 시간대와 무관하게 KST 로 표시한다 — 표에 "(KST)" 라고 적었으므로 값도 KST 여야 한다.
const formatKst = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('sv-SE', { timeZone: 'Asia/Seoul', hour12: false }).slice(0, 16);
};

const accountLabel = (acc) => `${acc.name || '이름없음'} (${acc.username})${acc.deptName ? ' · ' + acc.deptName : ''}`;

const RULE_STEPS = [
  { id: 'MASTER', condition: '사번 = dentium', result: '전체', tagColor: 'purple' },
  { id: 'MASTER_EQUIP', condition: '사번 = dentium03', result: '영업+장비 계약', tagColor: 'purple' },
  { id: 'CS_TEAM', condition: '소속 = 고객지원팀 또는 CS 엔지니어 명단', result: '설치확인서 전체', tagColor: 'green' },
  { id: 'QA_TEAM', condition: '소속 = 장비품질팀', result: '설치확인서 전체', tagColor: 'green' },
  { id: 'CENTER_HEAD', condition: '센터장', result: '센터와 직속 산하 지점 영업계약', tagColor: 'blue' },
  { id: 'BRANCH_HEAD', condition: '지점장·총무', result: '소속 지점 영업계약', tagColor: 'blue' },
  { id: 'STAFF_BRANCH', condition: '지점전체조회 권한 옵션 = Y', result: '소속 지점 영업계약', tagColor: 'blue' },
];

// 규칙 7단계 밖(그 외/STAFF_SELF)의 결과 라벨 — 사다리 종단 행과 '계정으로 확인' 요약 줄이 같은 문구를 쓰도록 공유
const FALLBACK_RULE_RESULT = '본인이 발행한 영업계약';

const CLASSIFICATION_LABELS = {
  MASTER: '마스터',
  MASTER_EQUIP: '장비 마스터',
  CS_TEAM: '고객지원(명단 포함)',
  QA_TEAM: '장비품질팀',
  CENTER_HEAD: '센터장',
  BRANCH_HEAD: '지점장·총무',
  STAFF_BRANCH: '지점전체조회',
  STAFF_SELF: '본인 발행',
};

// 직원별 조회 권한 탭 분류 필터 옵션 — CLASSIFICATION_LABELS 키 순서 그대로 사용
const CLASSIFICATION_KEYS = Object.keys(CLASSIFICATION_LABELS);

const SCOPE_LABELS = {
  SALES_DEPTS: '영업계약 · 지정 지점',
  CS_ALL: '설치확인서 · 전사',
};

const ACTION_LABELS = {
  ADD_CS_ENGINEER: 'CS 엔지니어 추가',
  REMOVE_CS_ENGINEER: 'CS 엔지니어 삭제',
  ADD_PERSONAL_GRANT: '개인 권한 추가',
  REMOVE_PERSONAL_GRANT: '개인 권한 삭제',
  ADD_DEPT_LINEAGE: '지점 계보 추가',
  REMOVE_DEPT_LINEAGE: '지점 계보 삭제',
};

// 추가 모달(step 1) 제목 — 삭제 모달은 항상 "삭제 사유 입력", step 2는 항상 "변경 미리보기" (actionModalTitle 참고)
const ADD_MODAL_TITLES = {
  grant: '개인 예외 추가',
  lineage: '지점 계보 추가',
  cs: 'CS 엔지니어 추가',
};

const CS_COLUMNS = [
  { title: '사번', dataIndex: 'username', key: 'username' },
  { title: '이름', dataIndex: 'name', key: 'name' },
  { title: '소속', dataIndex: 'deptName', key: 'deptName' },
  { title: '재직 상태', dataIndex: 'active', key: 'active' },
  { title: '관리', key: 'actions' },
];

const STAFF_COLUMNS = [
  { title: '사번', dataIndex: 'username', key: 'username', width: 110 },
  { title: '이름', dataIndex: 'name', key: 'name', width: 100 },
  { title: '소속', dataIndex: 'deptName', key: 'deptName' },
  { title: '직급', dataIndex: 'duty', key: 'duty', width: 90 },
  { title: '조회 범위', key: 'scope' },
  { title: '예외', key: 'exceptions', width: 160 },
  { title: '관리', key: 'actions', width: 120 },
];

const HISTORY_COLUMNS = [
  { title: '시각 (KST)', dataIndex: 'at', key: 'at' },
  { title: '계정', dataIndex: 'actor', key: 'actor' },
  { title: 'IP', dataIndex: 'ip', key: 'ip' },
  { title: '동작', dataIndex: 'action', key: 'action' },
  { title: '요약', dataIndex: 'summary', key: 'summary' },
  { title: '사유', dataIndex: 'reason', key: 'reason' },
];

const PREVIEW_COLUMNS = [
  { title: '사번', dataIndex: 'username', key: 'username' },
  { title: '이름', dataIndex: 'name', key: 'name' },
  { title: '소속', dataIndex: 'deptName', key: 'deptName' },
  { title: '직급', dataIndex: 'duty', key: 'duty' },
  { title: '분류', dataIndex: 'classification', key: 'classification' },
  { title: '조회 건수 (전 → 후)', key: 'count' },
];

export default {
  name: 'ContractViewPolicy',
  components: { PageHeader, VideoGuideModal },
  data() {
    return {
      loading: false,
      forbidden: false,
      submitting: false,
      policy: null,
      options: { currentDepts: [], retiredDepts: [] },
      activeTab: TAB_KEYS.includes(this.$route.query.tab) ? this.$route.query.tab : 'rules',

      accountOptions: [],
      accountSearchTimer: null,

      // "계정으로 확인" 사다리 하이라이트 전용 — 추가 폼(csAddForm 등)과 독립적으로 관리한다
      rulesProbe: { username: null, loading: false, result: null },

      // 직원별 조회 권한 탭 전용 — 서버 페이지네이션 목록 + 검색/필터 상태
      staffTable: { items: [], total: 0, loading: false, loaded: false },
      staffFilters: { query: '', classification: '', onlyExceptions: false },
      staffPage: 1,
      staffPageSize: 20,
      staffSearchTimer: null,
      staffExpandedRowKeys: [],

      csAddForm: { username: null, reason: '' },
      grantAddForm: { username: null, scope: 'SALES_DEPTS', depts: [], reason: '' },
      lineageAddForm: { fromDept: null, toDepts: [], effectiveDate: null, reason: '' },

      // 추가 3종 + 삭제 3종을 전부 이 한 모달로 처리한다 (mode: add|delete, step: 1 입력 → 2 변경 미리보기)
      actionModal: { visible: false, mode: 'add', type: null, step: 1, summary: '', reason: '', affected: [], makeRequest: null },

      // 동영상 가이드 모달
      videoGuideOpen: false,
      videoGuide: null,

      locale,
      RULE_STEPS,
      CLASSIFICATION_LABELS,
      classificationKeys: CLASSIFICATION_KEYS,
      SCOPE_LABELS,
      ACTION_LABELS,
      csColumns: CS_COLUMNS,
      staffColumns: STAFF_COLUMNS,
      historyColumns: HISTORY_COLUMNS,
      previewColumns: PREVIEW_COLUMNS,
    };
  },
  computed: {
    // 개인 권한 지점 선택지 = 현재 지점 ∪ 폐지 지점 (스펙 4번 항목)
    grantDeptOptions() {
      return [...new Set([...(this.options.currentDepts || []), ...(this.options.retiredDepts || [])])];
    },
    rulesProbeSummary() {
      const r = this.rulesProbe.result;
      if (!r) return '';
      const acc = r.account || {};
      const ruleStep = RULE_STEPS.find((s) => s.id === r.classification);
      const resultLabel = ruleStep ? ruleStep.result : FALLBACK_RULE_RESULT;
      const addons = [];
      if (r.appliedGrants && r.appliedGrants.length) addons.push(`개인 권한 ${r.appliedGrants.length}건`);
      if (r.appliedLineages && r.appliedLineages.length) addons.push(`지점 계보 ${r.appliedLineages.length}건`);
      const addonText = addons.length ? ` (+ ${addons.join(', ')})` : '';
      return `${acc.name || '이름없음'} (${acc.username}) · ${acc.deptName || '-'} · ${acc.duty || '-'} → ${r.step}번 · ${resultLabel}${addonText} · 조회 가능 ${r.contractCount}건`;
    },
    actionModalTitle() {
      if (this.actionModal.step === 2) return '변경 미리보기';
      return this.actionModal.mode === 'delete' ? '삭제 사유 입력' : ADD_MODAL_TITLES[this.actionModal.type] || '';
    },
    staffPagination() {
      return {
        current: this.staffPage,
        pageSize: this.staffPageSize,
        total: this.staffTable.total,
        showSizeChanger: true,
        pageSizeOptions: ['20', '50', '100'],
        showTotal: (total) => `총 ${total}명`,
      };
    },
    // 자동 규칙 탭 addon 카드용 미리보기 — 최대 5건 + 나머지 건수
    personalGrantsPreview() {
      const items = (this.policy && this.policy.personalGrants) || [];
      return { shown: items.slice(0, 5), restCount: Math.max(items.length - 5, 0) };
    },
    deptLineagesPreview() {
      const items = (this.policy && this.policy.deptLineages) || [];
      return { shown: items.slice(0, 5), restCount: Math.max(items.length - 5, 0) };
    },
  },
  async mounted() {
    // 동영상 가이드 데이터를 미리 받아 둔다 — 모달을 열 때 네트워크 대기 없이 바로 뜨도록
    getVideoGuide().then((g) => { this.videoGuide = g; });
    await this.loadAll();
    if (this.activeTab === 'grants') this.fetchStaffList();
  },
  methods: {
    formatKst,
    accountLabel,
    // 동영상 가이드 버튼 — 가이드 데이터를 준비해 모달을 연다
    async openVideoGuide() {
      if (!this.videoGuide) this.videoGuide = await getVideoGuide();
      this.videoGuideOpen = true;
    },
    async loadAll() {
      this.loading = true;
      try {
        const [policy, options] = await Promise.all([getPolicy(), getOptions()]);
        this.policy = policy;
        this.options = options;
      } catch (error) {
        if (error?.response?.status === 403) {
          this.forbidden = true;
        } else {
          message.error('정책을 불러오지 못했습니다.');
        }
      } finally {
        this.loading = false;
      }
    },
    // 409 충돌 후 최신 정책만 다시 받아온다 (전체 재조회는 과함)
    async reloadPolicy() {
      try {
        this.policy = await getPolicy();
      } catch (error) {
        // 이미 위에서 충돌 메시지를 띄웠으므로 재조회 실패는 조용히 무시
      }
    },
    onTabChange(key) {
      this.$router.replace({ query: { ...this.$route.query, tab: key } }).catch(() => {});
      if (key === 'grants' && !this.staffTable.loaded) this.fetchStaffList();
    },
    // 규칙 요약 ladder 의 "CS 엔지니어 명단"/"관리 →" 링크 전용 — 탭 클릭과 동일하게
    // activeKey 갱신 + onTabChange 로 ?tab= 동기화까지 재사용한다.
    goToTab(key) {
      this.activeTab = key;
      this.onTabChange(key);
    },
    onAccountSearch(query) {
      clearTimeout(this.accountSearchTimer);
      const trimmed = (query || '').trim();
      if (!trimmed) {
        this.accountOptions = [];
        return;
      }
      this.accountSearchTimer = setTimeout(async () => {
        try {
          this.accountOptions = await searchAccounts(trimmed.slice(0, 50));
        } catch (error) {
          this.accountOptions = [];
        }
      }, DEBOUNCE_MS);
    },
    async onRulesProbeSelect(username) {
      this.rulesProbe.username = username;
      this.rulesProbe.result = null;
      if (!username) return;
      this.rulesProbe.loading = true;
      try {
        this.rulesProbe.result = await evaluateAccount(username);
      } catch (error) {
        const status = error?.response?.status;
        message.error(error?.response?.data?.message || (status === 404 ? '존재하지 않는 계정입니다.' : '조회 중 오류가 발생했습니다.'));
      } finally {
        this.rulesProbe.loading = false;
      }
    },
    onRulesProbeClear() {
      this.rulesProbe.username = null;
      this.rulesProbe.result = null;
    },
    // classification(문자열) 기준 매칭 — step 번호가 아니라 id 로 비교해 ladder 순서 변경에도 안전하다
    rulesStepClass(step, index) {
      const r = this.rulesProbe.result;
      if (!r) return '';
      if (r.classification === step.id) return 'rules-step--matched';
      const matchedIndex = RULE_STEPS.findIndex((s) => s.id === r.classification);
      if (matchedIndex === -1) return 'rules-step--passed'; // STAFF_SELF: 7단계 전부 지나감
      return index < matchedIndex ? 'rules-step--passed' : 'rules-step--dimmed';
    },
    rulesConnectorClass(index) {
      const r = this.rulesProbe.result;
      if (!r) return '';
      const matchedIndex = RULE_STEPS.findIndex((s) => s.id === r.classification);
      if (matchedIndex === -1) return 'rules-connector--passed';
      return index < matchedIndex ? 'rules-connector--passed' : 'rules-connector--dimmed';
    },
    rulesFallbackClass() {
      return this.rulesProbe.result && this.rulesProbe.result.classification === 'STAFF_SELF' ? 'rules-step--matched' : '';
    },
    // 조회 범위 문장 조립 — 직원별 조회 권한 탭 표 + 상세 펼침에서 공유
    scopeSentence(scope) {
      if (!scope) return '-';
      let base;
      if (scope.types === 'ALL') base = '전체 계약';
      else if (scope.types === 'SALES_EQUIP') base = '영업+장비 계약';
      else if (scope.types === 'CS') base = '설치확인서 전체';
      else if (scope.self) base = '본인이 발행한 영업계약';
      else {
        const lineageSet = new Set(scope.lineageDepts || []);
        const grantSet = new Set(scope.grantDepts || []);
        const core = (scope.depts || []).filter((d) => !lineageSet.has(d) && !grantSet.has(d));
        base = `${core.join(' · ')} 영업계약`;
      }
      const extras = [];
      if (scope.lineageDepts && scope.lineageDepts.length) extras.push(`옛 ${scope.lineageDepts.join(', ')}`);
      if (scope.grantDepts && scope.grantDepts.length) extras.push(`${scope.grantDepts.join(', ')}(개인 예외)`);
      if (scope.csAllByGrant) extras.push('설치확인서 전체(개인 예외)');
      return [base, ...extras].join(' + ');
    },
    async fetchStaffList(resetPage) {
      if (resetPage) this.staffPage = 1;
      this.staffTable.loading = true;
      try {
        const { total, items } = await listAccountEvaluations({
          query: this.staffFilters.query.trim(),
          classification: this.staffFilters.classification,
          onlyExceptions: this.staffFilters.onlyExceptions,
          page: this.staffPage,
          limit: this.staffPageSize,
        });
        this.staffTable.items = items || [];
        this.staffTable.total = total || 0;
        this.staffTable.loaded = true;
      } catch (error) {
        message.error('직원 목록을 불러오지 못했습니다.');
      } finally {
        this.staffTable.loading = false;
      }
    },
    onStaffTableChange(pagination) {
      this.staffPage = pagination.current;
      this.staffPageSize = pagination.pageSize;
      this.fetchStaffList();
    },
    onStaffQueryInput() {
      clearTimeout(this.staffSearchTimer);
      this.staffSearchTimer = setTimeout(() => this.fetchStaffList(true), STAFF_SEARCH_DEBOUNCE_MS);
    },
    onStaffQueryEnter() {
      clearTimeout(this.staffSearchTimer);
      this.fetchStaffList(true);
    },
    onStaffFilterChange() {
      this.fetchStaffList(true);
    },
    onStaffExpand(expanded, record) {
      if (expanded) {
        if (!this.staffExpandedRowKeys.includes(record.username)) this.staffExpandedRowKeys = [...this.staffExpandedRowKeys, record.username];
      } else {
        this.staffExpandedRowKeys = this.staffExpandedRowKeys.filter((k) => k !== record.username);
      }
    },
    // 표 행의 "예외 추가" — 개인 예외 추가 모달을 그 직원으로 미리 채워서 연다
    openGrantAddForStaff(record) {
      this.openAddModal('grant');
      this.grantAddForm.username = record.username;
      if (!this.accountOptions.some((a) => a.username === record.username)) {
        this.accountOptions = [{ username: record.username, name: record.name, deptName: record.deptName }, ...this.accountOptions];
      }
    },
    // "판정 경로 보기" — 규칙 요약(자동 규칙) 탭으로 이동해 같은 계정으로 사다리를 하이라이트한다
    onViewJudgmentPath(record) {
      if (!this.accountOptions.some((a) => a.username === record.username)) {
        this.accountOptions = [{ username: record.username, name: record.name, deptName: record.deptName }, ...this.accountOptions];
      }
      this.goToTab('rules');
      this.onRulesProbeSelect(record.username);
    },
    // 자동 규칙 addon 카드에서 재사용 — 이름이 없으면 사번으로 대체
    personalGrantLine(g) {
      const who = `${g.name || g.username} (${g.username})`;
      const what = g.scope === 'CS_ALL' ? '설치확인서 전체 추가' : `${(g.depts || []).join(', ')}(영업계약) 추가`;
      return `${who} — ${what}`;
    },
    lineageSummary(l) {
      return `${l.fromDept} → ${(l.toDepts || []).join(', ')}`;
    },
    onDeptsChange(formObj, field, value) {
      if (value.length > 10) {
        message.warning('지점은 최대 10개까지 선택할 수 있습니다.');
        formObj[field] = value.slice(0, 10);
      } else {
        formObj[field] = value;
      }
    },
    validateReason(reason) {
      const len = (reason || '').trim().length;
      if (len < 1 || len > 300) {
        message.error('사유는 1자 이상 300자 이하로 입력해 주세요.');
        return false;
      }
      return true;
    },
    resetCsForm() {
      this.csAddForm = { username: null, reason: '' };
    },
    resetGrantForm() {
      this.grantAddForm = { username: null, scope: 'SALES_DEPTS', depts: [], reason: '' };
    },
    resetLineageForm() {
      this.lineageAddForm = { fromDept: null, toDepts: [], effectiveDate: null, reason: '' };
    },
    resetFormFor(type) {
      if (type === 'grant') this.resetGrantForm();
      else if (type === 'lineage') this.resetLineageForm();
      else if (type === 'cs') this.resetCsForm();
    },
    handleMutationError(error) {
      const status = error?.response?.status;
      if (status === 409) {
        message.error('다른 사용자가 먼저 수정했습니다. 새로고침합니다.');
        this.reloadPolicy();
      } else if (status === 400) {
        message.error(error?.response?.data?.message || '요청을 처리할 수 없습니다.');
      } else {
        message.error('처리 중 오류가 발생했습니다.');
      }
      return status;
    },
    openAddModal(type) {
      this.resetFormFor(type);
      this.accountOptions = [];
      this.actionModal = { visible: true, mode: 'add', type, step: 1, summary: '', reason: '', affected: [], makeRequest: null };
    },
    openDeleteModal(type, summary, makeRequest) {
      this.actionModal = { visible: true, mode: 'delete', type, step: 1, summary, reason: '', affected: [], makeRequest };
    },
    closeActionModal() {
      this.actionModal.visible = false;
    },
    onActionPreview() {
      if (this.actionModal.mode === 'delete') {
        if (!this.validateReason(this.actionModal.reason)) return;
        this.runPreview();
        return;
      }
      if (this.actionModal.type === 'grant') this.previewAddGrant();
      else if (this.actionModal.type === 'lineage') this.previewAddLineage();
      else if (this.actionModal.type === 'cs') this.previewAddCs();
    },
    // actionModal.makeRequest(dryRun) => Promise<응답 데이터> — 추가 3종 + 삭제 3종 모두 이 한 함수로 미리보기를 띄운다
    async runPreview() {
      this.submitting = true;
      try {
        const data = await this.actionModal.makeRequest(true);
        this.actionModal.affected = data.affected || [];
        this.actionModal.step = 2;
      } catch (error) {
        if (this.handleMutationError(error) === 409) this.actionModal.visible = false;
      } finally {
        this.submitting = false;
      }
    },
    async onActionConfirm() {
      this.submitting = true;
      try {
        this.policy = await this.actionModal.makeRequest(false);
        this.actionModal.visible = false;
        message.success('저장되었습니다.');
        if (this.actionModal.mode === 'add') this.resetFormFor(this.actionModal.type);
        if (this.staffTable.loaded) this.fetchStaffList();
      } catch (error) {
        if (this.handleMutationError(error) === 409) this.actionModal.visible = false;
      } finally {
        this.submitting = false;
      }
    },
    previewAddCs() {
      if (!this.csAddForm.username) {
        message.error('계정을 선택해 주세요.');
        return;
      }
      if (!this.validateReason(this.csAddForm.reason)) return;
      const { username, reason } = this.csAddForm;
      const acc = this.accountOptions.find((a) => a.username === username);
      this.actionModal.summary = acc ? `${acc.name || '이름없음'} (${username})` : username;
      this.actionModal.makeRequest = (dryRun) => addCsEngineer({ username, reason, expectedVersion: this.policy.version, dryRun });
      this.runPreview();
    },
    onRemoveCs(row) {
      this.openDeleteModal('cs', `${row.name || '이름없음'} (${row.username})`, (dryRun) =>
        removeCsEngineer(row.username, { reason: this.actionModal.reason, expectedVersion: this.policy.version, dryRun })
      );
    },
    previewAddGrant() {
      if (!this.grantAddForm.username) {
        message.error('계정을 선택해 주세요.');
        return;
      }
      if (this.grantAddForm.scope === 'SALES_DEPTS' && this.grantAddForm.depts.length === 0) {
        message.error('지점을 1개 이상 선택해 주세요.');
        return;
      }
      if (!this.validateReason(this.grantAddForm.reason)) return;
      const { username, scope, depts, reason } = this.grantAddForm;
      const body = { username, scope, reason, expectedVersion: this.policy.version };
      if (scope === 'SALES_DEPTS') body.depts = depts;
      const acc = this.accountOptions.find((a) => a.username === username);
      const who = acc ? `${acc.name || '이름없음'} (${username})` : username;
      this.actionModal.summary = scope === 'SALES_DEPTS' ? `${who} · ${SCOPE_LABELS[scope]} [${depts.join(', ')}]` : `${who} · ${SCOPE_LABELS[scope]}`;
      this.actionModal.makeRequest = (dryRun) => addPersonalGrant({ ...body, dryRun });
      this.runPreview();
    },
    onRemoveGrant(row) {
      this.openDeleteModal('grant', `${row.name || '이름없음'} (${row.username}) · ${SCOPE_LABELS[row.scope] || row.scope}`, (dryRun) =>
        removePersonalGrant(row.id, { reason: this.actionModal.reason, expectedVersion: this.policy.version, dryRun })
      );
    },
    previewAddLineage() {
      if (!this.lineageAddForm.fromDept) {
        message.error('폐지 지점을 선택해 주세요.');
        return;
      }
      if (!this.lineageAddForm.toDepts.length) {
        message.error('후속 지점을 1개 이상 선택해 주세요.');
        return;
      }
      if (!this.lineageAddForm.effectiveDate) {
        message.error('발령일을 선택해 주세요.');
        return;
      }
      if (!this.validateReason(this.lineageAddForm.reason)) return;
      const { fromDept, toDepts, effectiveDate, reason } = this.lineageAddForm;
      this.actionModal.summary = `${fromDept} → ${toDepts.join(', ')} · 발령일 ${effectiveDate}`;
      this.actionModal.makeRequest = (dryRun) =>
        addDeptLineage({ fromDept, toDepts, effectiveDate, reason, expectedVersion: this.policy.version, dryRun });
      this.runPreview();
    },
    onRemoveLineage(row) {
      this.openDeleteModal('lineage', `${row.fromDept} → ${row.toDepts.join(', ')}`, (dryRun) =>
        removeDeptLineage(row.id, { reason: this.actionModal.reason, expectedVersion: this.policy.version, dryRun })
      );
    },
  },
};
</script>

<style scoped>
.forbidden-box {
  padding: 60px 24px;
  text-align: center;
  color: #4b5158;
  font-size: 15px;
}

.legacy-banner {
  margin-bottom: 16px;
}

.tabs-card {
  padding: 20px 24px;
}

.tab-note {
  margin-bottom: 12px;
}

.rules-ladder__heading {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2329;
}

.rules-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.rules-probe {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.rules-probe__label {
  font-size: 13px;
  font-weight: 600;
  color: #4b5158;
}

.rules-probe__summary {
  margin: 0 0 20px;
  padding: 10px 12px;
  background: #f7f8fa;
  border: 1px solid #eceef1;
  border-radius: 6px;
  font-size: 13px;
  color: #1f2329;
}

.rules-probe__summary :deep(.ant-tag) {
  margin-left: 6px;
}

.rules-step {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rules-step__badge {
  flex: 0 0 28px;
  height: 28px;
  border-radius: 50%;
  background: #1f2329;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rules-step__badge--terminal {
  background: transparent;
  color: #8d939a;
  font-size: 16px;
}

.rules-step__condition {
  flex: 1 1 auto;
  min-width: 0;
  padding: 10px 14px;
  background: #f7f8fa;
  border: 1px solid #eceef1;
  border-radius: 6px;
  font-size: 13px;
  color: #1f2329;
}

.rules-step__link {
  color: #1890ff;
  cursor: pointer;
}

.rules-step__link:hover {
  text-decoration: underline;
}

.rules-step__branch {
  flex: 0 0 auto;
  min-width: 220px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rules-step__branch-label {
  font-size: 12px;
  font-weight: 600;
  color: #389e0d;
}

.rules-step__arrow {
  color: #c0c4cc;
  font-size: 16px;
}

.rules-step--passed {
  opacity: 0.55;
}

.rules-step--matched {
  outline: 2px solid #1890ff;
  outline-offset: 2px;
  border-radius: 8px;
}

.rules-step--matched .rules-step__branch-label,
.rules-step--matched .rules-step__arrow {
  color: #1890ff;
  font-weight: 700;
}

.rules-step--matched .rules-step__branch {
  filter: drop-shadow(0 0 4px rgba(24, 144, 255, 0.5));
}

.rules-step--dimmed {
  opacity: 0.35;
}

.rules-connector {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 13px;
  padding: 4px 0;
}

.rules-connector__line {
  width: 2px;
  height: 16px;
  background: #d9d9d9;
}

.rules-connector__label {
  font-size: 12px;
  color: #8d939a;
}

.rules-connector--passed .rules-connector__line {
  background: #6e747c;
}

.rules-connector--passed .rules-connector__label {
  color: #1f2329;
  font-weight: 700;
}

.rules-connector--dimmed {
  opacity: 0.35;
}

.rules-addons {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 24px;
}

.rules-addon-card {
  flex: 1 1 280px;
  padding: 14px 16px;
  background: #fafafa;
  border: 1px solid #eceef1;
  border-radius: 8px;
}

.rules-addon-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.rules-addon-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2329;
}

.rules-addon-card__desc {
  margin: 6px 0 0;
  font-size: 12px;
  color: #6e747c;
  line-height: 1.6;
}

.rules-addon-card--highlight {
  border-color: #1890ff;
  background: #e6f4ff;
}

.rules-addon-card__applied {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 12px;
  color: #1f2329;
}

.rules-addon-card__applied li {
  margin-bottom: 2px;
}

.rules-footnote {
  margin-top: 16px;
  font-size: 12px;
  color: #8d939a;
}

.tab-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.staff-tab-intro {
  margin: 0 0 16px;
  font-size: 13px;
  color: #6e747c;
  line-height: 1.6;
}

.staff-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.staff-toolbar__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.staff-tag-small {
  margin-left: 6px;
}

.staff-more {
  color: #8d939a;
}

.staff-detail {
  padding: 4px 8px 12px;
  font-size: 13px;
  color: #1f2329;
}

.staff-detail__line {
  margin: 0 0 8px;
}

.staff-detail__block {
  margin: 12px 0;
}

.staff-detail__label {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 600;
  color: #4b5158;
}

.staff-detail__list {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  line-height: 1.8;
}

.staff-lineage-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.staff-lineage-card {
  padding: 14px 16px;
  background: #fafafa;
  border: 1px solid #eceef1;
  border-radius: 8px;
}

.staff-lineage-card__main {
  margin: 0 0 8px;
  font-size: 13px;
  color: #1f2329;
  line-height: 1.6;
}

.staff-lineage-card__meta {
  margin: 0 0 4px;
  font-size: 12px;
  color: #6e747c;
}

.link-action {
  color: #1890ff;
  cursor: pointer;
  margin-right: 12px;
}

.link-action:hover {
  text-decoration: underline;
}

.policy-modal-body {
  padding: 20px 24px 16px;
  max-height: 80vh;
  overflow-y: auto;
}

.policy-modal-body :deep(.ant-form-item) {
  margin-bottom: 16px;
}

.policy-modal-body :deep(.ant-form-item-label > label) {
  font-weight: 600;
  font-size: 13px;
  color: #4b5158;
}

.policy-modal-body :deep(.ant-input) {
  width: 100%;
}

.policy-modal-body :deep(.ant-select-multiple .ant-select-selector) {
  height: auto !important;
  min-height: 40px;
}

.modal-summary {
  margin-bottom: 12px;
  padding: 10px 12px;
  color: #1f2329;
  font-weight: 600;
  background: #f7f8fa;
  border-radius: 6px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.no-affected {
  text-align: center;
  color: #8d939a;
  padding: 24px 0;
}

.count-delta.up {
  color: #389e0d;
  font-weight: 600;
}

.count-delta.down {
  color: #d4380d;
  font-weight: 600;
}

/* 모바일 */
@media screen and (max-width: 767px) {
  .tabs-card {
    padding: 16px;
  }

  .rules-step {
    flex-direction: column;
    align-items: stretch;
  }

  .rules-step__branch {
    min-width: 0;
    padding-left: 40px;
  }

  .rules-connector {
    margin-left: 0;
  }
}
</style>

<!--
  wrap-class-name 은 antd 가 모달 바깥 래퍼(.ant-modal-wrap)에 붙이는 클래스라
  위 scoped 블록의 :deep() 으로는 닿지 않는다(우리 템플릿의 자손이 아니라 조상 쪽).
  레포 관례인 "modal-auto-height" 클래스는 src/ 전수 grep 결과 실제 CSS 선언이 어디에도 없다
  (ProductDeviceFormModal 등 6개 파일이 전부 wrap-class-name 만 쓸 뿐 매칭되는 규칙이 없음).
  다른 화면을 먼저 방문했는지에 기대지 않도록 이 화면 전용 네임스페이스로 직접 override한다.
-->
<style>
.contract-view-policy-modal .ant-modal-content {
  height: auto !important;
  min-height: 0 !important;
}
</style>
